import { useState } from 'react';
import { toast } from 'sonner';
import type { AssignableOrgRole } from '@hangar/shared';
import { useMe, useMemberMutations, useMembers } from '@/api/hooks';
import type { Member } from '@/api/schemasMembers';
import { CompactTable, type CompactColumn } from '@/components/CompactTable';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import type { RowAction } from '@/components/RowActions';
import { Tag } from '@/components/badges';
import { UserAvatar } from '@/components/UserAvatar';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { showApiFailure } from '@/features/access/accessToasts';
import { formatDate } from '@/lib/format';
import { InviteDialog } from './InviteDialog';
import { PendingInvitations } from './PendingInvitations';
import { ROLE_OPTIONS, memberRowCan, roleLabel } from './membersModel';

/** Members tab of Access (admins): the people of the org, their role, and the open invitations. */
export function MembersPanel() {
  return (
    <div data-testid="members-panel" className="flex min-w-0 flex-col gap-6">
      <MembersCard />
      <PendingInvitations />
    </div>
  );
}

function MembersCard() {
  const members = useMembers();
  return (
    <Card className="enter">
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <CardTitle className="flex items-center gap-2">
            Members
            {members.data ? <span data-testid="members-count"><Tag>{members.data.length}</Tag></span> : null}
          </CardTitle>
          <CardDescription>Who can sign in to this organization, and what they may do. A change applies from their next request.</CardDescription>
        </div>
        <InviteDialog />
      </CardHeader>
      <CardContent>
        <MembersBody members={members} />
      </CardContent>
    </Card>
  );
}

function MembersBody({ members }: { members: ReturnType<typeof useMembers> }) {
  if (members.isPending) return <MembersSkeleton />;
  if (members.isError) return <ErrorState compact error={members.error} onRetry={() => void members.refetch()} title="Couldn't load the members" />;
  if (members.data.length <= 1) {
    return (
      <>
        {members.data.length ? <MembersTable rows={members.data} /> : null}
        <EmptyState testId="members-empty" compact icon="group_add" title="You are the only one here" description="Invite your team: each person gets one email to sign in and joins with the role you choose." />
      </>
    );
  }
  return <MembersTable rows={members.data} />;
}

function MembersSkeleton() {
  return (
    <div data-testid="members-loading" className="flex flex-col gap-2" aria-busy="true" aria-label="Loading members">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-3 rounded-lg border p-3">
          <Skeleton className="size-8 rounded-full" />
          <div className="flex flex-1 flex-col gap-1.5">
            <Skeleton className="h-3.5 w-40" />
            <Skeleton className="h-3 w-56" />
          </div>
          <Skeleton className="h-6 w-16" />
        </div>
      ))}
    </div>
  );
}

function Person({ m }: { m: Member }) {
  // A user who joined without a name carries the email as name: print it once.
  const name = m.user.name && m.user.name !== m.user.email ? m.user.name : null;
  return (
    <div className="flex min-w-0 items-center gap-3">
      <UserAvatar name={name} email={m.user.email} size={32} />
      <div className="min-w-0">
        <div className={name ? 'truncate text-sm font-medium text-foreground' : 'truncate font-mono text-[13px] text-foreground'}>{name ?? m.user.email}</div>
        {name ? <div className="truncate font-mono text-xs text-subtle">{m.user.email}</div> : null}
      </div>
    </div>
  );
}

const lastSeen = (m: Member) => (m.lastLoginAt ? `Last sign-in ${formatDate(m.lastLoginAt)}` : 'Never signed in');

function MembersTable({ rows }: { rows: Member[] }) {
  const me = useMe().data;
  const { update } = useMemberMutations();
  const [removing, setRemoving] = useState<Member | null>(null);
  const changeRole = async (m: Member, role: AssignableOrgRole) => {
    try {
      await update.mutateAsync({ userId: m.user.id, role });
      toast.success(`${m.user.name || m.user.email} is now ${roleLabel(role)}`);
    } catch (e) {
      showApiFailure(e, 'Could not change the role');
    }
  };
  const actions = (m: Member): RowAction[] => {
    const can = memberRowCan(m, me);
    const roles = ROLE_OPTIONS.filter((o) => o.value !== m.role).map((o) => ({
      key: `role-${o.value}`, label: `Make ${o.label.toLowerCase()}`, icon: 'badge', testId: `member-role-${o.value}`,
      onSelect: () => void changeRole(m, o.value), ...(can.changeRole.allowed ? {} : { disabledReason: can.changeRole.reason }),
    }));
    return [...roles, { key: 'remove', label: 'Remove access', icon: 'person_remove', destructive: true, testId: 'member-remove', onSelect: () => setRemoving(m), ...(can.remove.allowed ? {} : { disabledReason: can.remove.reason }) }];
  };
  const columns: CompactColumn<Member>[] = [
    { key: 'person', header: 'Person', width: 'w-[45%]', className: 'max-w-0', cell: (m) => <Person m={m} /> },
    { key: 'role', header: 'Role', cell: (m) => <RoleCell m={m} /> },
    { key: 'seen', header: 'Activity', hide: '@max-[40rem]:hidden', cell: (m) => <span className="text-xs text-muted-foreground">{lastSeen(m)}</span> },
  ];
  return (
    <>
      <CompactTable
        label="Members"
        rows={rows}
        columns={columns}
        rowKey={(m) => m.user.id}
        rowTestId={(m) => `member-row-${m.user.email}`}
        subject={(m) => m.user.name || m.user.email}
        actions={actions}
        actionsVariant="menu"
        phoneRow={(m) => (
          <div className="flex flex-col gap-2">
            <Person m={m} />
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground"><RoleCell m={m} /> {lastSeen(m)}</div>
          </div>
        )}
      />
      <RemoveMemberDialog member={removing} onDone={() => setRemoving(null)} />
    </>
  );
}

function RoleCell({ m }: { m: Member }) {
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <Tag className={m.role === 'admin' || m.role === 'owner' ? 'text-awaiting' : undefined}>{roleLabel(m.role)}</Tag>
      {m.user.isExternal ? <Tag title={m.user.company ?? undefined}>External</Tag> : null}
      {m.expiresAt ? <span className="text-xs text-subtle">until {formatDate(m.expiresAt)}</span> : null}
    </span>
  );
}

function RemoveMemberDialog({ member, onDone }: { member: Member | null; onDone: () => void }) {
  const { remove } = useMemberMutations();
  const who = member ? member.user.name || member.user.email : '';
  const onRemove = async () => {
    if (!member) return;
    try {
      await remove.mutateAsync(member.user.id);
      toast.success(`${who} no longer has access`, { description: 'Their next request is refused; their personal API tokens stop working too.' });
      onDone();
    } catch (e) {
      showApiFailure(e, 'Could not remove the member');
    }
  };
  return (
    <AlertDialog open={Boolean(member)} onOpenChange={(o) => (o ? undefined : onDone())}>
      <AlertDialogContent data-testid="member-remove-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="break-words">Remove {who}?</AlertDialogTitle>
          <AlertDialogDescription>They lose access to this organization at once, and their personal API tokens stop working. You can invite them again later.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="member-remove-dismiss">Keep access</AlertDialogCancel>
          <AlertDialogAction destructive data-testid="member-remove-confirm" disabled={remove.isPending} onClick={(e) => { e.preventDefault(); void onRemove(); }}>
            Remove access
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
